import React, { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setLoanAmmount,
  setTimePeriod,
  setIntrestRate,
  setEmi,
  resetEducationData,
  setTotalAmount,
} from "../redux/calculations/educationSlice";
import {
  resetChartData,
  updatedEducationChart,
} from "../redux/calculations/calculaterSlice";
import CalculatorLayout from "../components/CalcLayout";
import Input from "../components/Input";
import { Button } from "../components/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import useLocale from "../../src/locales/index";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import i18n from "../i18n";
import { useTranslation } from "react-i18next";
import DecimalUtils from "../utiles/decimalFormate";
import { Helmet } from "react-helmet";

const EducationCalc = () => {
  const dispatch = useDispatch();
  const { loanAmmount, intrestRate, timePeriod } = useSelector(
    (state) => state.educationCalculator
  );
  const { t } = useTranslation();
  const { textData } = useLocale();
  const { educationCalc } = textData;
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);
  const [calcDone, setCalcDone] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [isFormModified, setIsFormModified] = useState(false);

  const formik = useFormik({
    initialValues: {
      loanAmmount: loanAmmount,
      intrestRate: intrestRate,
      timePeriod: timePeriod,
    },
    validationSchema: Yup.object({
      loanAmmount: Yup.number()
        .min(100000, educationCalc.loanAmountMin)
        .max(5000000, educationCalc.loanAmountMax)
        .required(educationCalc.loanAmountRequired),
      intrestRate: Yup.number()
        .min(1, educationCalc.interestRateMin)
        .max(20, educationCalc.interestRateMax)
        .required(educationCalc.interestRateRequired),
      timePeriod: Yup.number()
        .min(1, educationCalc.timePeriodMax)
        .max(20, educationCalc.timePeriodMin)
        .required(educationCalc.timePeriodRequired),
    }),
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: (values) => {
      if (cookieAccept && analytics) {
        logEvent(analytics, "calculate_EDUCATION");
      }
      const principal = parseFloat(values.loanAmmount);
      const rate = parseFloat(values.intrestRate) / 1200;
      const time = parseFloat(values.timePeriod) * 12;

      const emi =
        (principal * rate * Math.pow(1 + rate, time)) /
        (Math.pow(1 + rate, time) - 1);
      const totalPayment = emi * time;
      const totalInterest = totalPayment - principal;

      dispatch(setLoanAmmount(values.loanAmmount));
      dispatch(setIntrestRate(values.intrestRate));
      dispatch(setTimePeriod(values.timePeriod));
      dispatch(setEmi(emi));
      dispatch(setTotalAmount(totalPayment));
      dispatch(
        updatedEducationChart({
          loanAmount: principal,
          totalInterest: totalInterest,
          emi: emi,
          totalPayment: totalPayment,
        })
      );

      saveToHistory({
        intrestRate: rate,
        tenture: time,
        emi: emi,
        tabName: "educationLoanCalc",
        totalInvestment: principal,
        yearlyReturn: totalInterest,
        interestRate: values.intrestRate,
        tenure: values.time,
        maturityAmount: totalPayment,
        calculationType: "Education Loan",
      });
      setShowTable(false);
      setCalcDone(true);
      setIsFormModified(false);
    },
  });
  useEffect(() => {
    return () => {
      dispatch(resetChartData());
      dispatch(resetEducationData());
    };
  }, [dispatch]);
  const saveToHistory = (calculationData) => {
    try {
      const existingData =
        JSON.parse(localStorage.getItem(textData.storeData)) || [];

      const newHistoryRecord = {
        intrestRate: calculationData.intrestRate,
        tenture: calculationData.tenture,
        tabName: calculationData.tabName,
        emi: calculationData.emi,
        totalInvestment: calculationData.totalInvestment,
        yearlyReturn: calculationData.yearlyReturn,
        interestRate: calculationData.interestRate,
        tenure: calculationData.tenure,
        maturityAmount: calculationData.maturityAmount,
        calculationType: calculationData.calculationType,
        calculationDate: new Date().toISOString(),
      };

      const isDuplicate = existingData.some((recordArray) => {
        if (!recordArray || !recordArray[0]) return false;
        const record = recordArray[0];

        return (
          record.calculationType === newHistoryRecord.calculationType &&
          record.totalInvestment === newHistoryRecord.totalInvestment &&
          record.interestRate === newHistoryRecord.interestRate &&
          record.tenure === newHistoryRecord.tenure &&
          record.maturityAmount === newHistoryRecord.maturityAmount
        );
      });
      if (!isDuplicate) {
        existingData.push([newHistoryRecord]);
        localStorage.setItem(textData.storeData, JSON.stringify(existingData));
      } else {
      }
    } catch (error) {}
  };

  const sliderTrackRef = useRef(null);
  const interestSliderRef = useRef(null);
  const periodSliderRef = useRef(null);

  const handleTimePeriodSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setTimePeriod(value));
    formik.setFieldValue("timePeriod", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleIntrestRatetSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setIntrestRate(value));
    formik.setFieldValue("intrestRate", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleLoanAmountSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setLoanAmmount(value));
    formik.setFieldValue("loanAmmount", value);
    setIsFormModified(true);
    setCalcDone(false);
  };
  const formatToINR = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";

    const shouldForceIndianFormat = i18n.language === "fa";

    return isNaN(Number(value))
      ? ""
      : Number(value).toLocaleString(
          shouldForceIndianFormat ? "en-IN" : i18n.language || "en-IN"
        );
  };

  const handleTimeChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue.length > 2) return;
    formik.setFieldValue("timePeriod", rawValue);
    if (rawValue === "") return;
    const numericValue = Number(rawValue);
    if (!isNaN(numericValue)) {
      const clamped = Math.max(1, Math.min(30, numericValue));
      dispatch(setTimePeriod(clamped));
    }
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleInterestRateChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("intrestRate", rawValue);
      dispatch(setIntrestRate(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;

    // Store normalized value
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);
    formik.setFieldValue("intrestRate", normalizedValue);
    dispatch(setIntrestRate(normalizedValue));
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleInvestmentChange = (e) => {
    const rawValue = e.target.value.replace(/[^0-9.,]/g, "");
    if (rawValue.length > 9) return;
    if (rawValue === "") {
      formik.setFieldValue("loanAmmount", "");
      dispatch(setLoanAmmount(""));
      return;
    }
    const numericValue = parseFloat(
      rawValue
        .replace(/,/g, ".")
        .replace(/\./g, "")
        .replace(/(\d+)(\.\d{2})?/, "$1.$2")
    ); // Convert to a number

    formik.setFieldValue("loanAmmount", numericValue);

    dispatch(setLoanAmmount(numericValue));
    setIsFormModified(true);
    setCalcDone(false);
  };

  useEffect(() => {
    dispatch(setLoanAmmount(formik.values.loanAmmount));
    dispatch(setTimePeriod(formik.values.timePeriod));
    dispatch(setIntrestRate(formik.values.intrestRate));
  }, [formik.values, dispatch]);
  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };

  const descriptionContent = (
    <div className="p-4 sm:p-5 w-full bg-[#F6F8FB] rounded-xl">
      <h2 className="text-black text-base sm:text-xl font-medium">
        {" "}
        {textData.educationContent.title}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.educationContent.description}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {" "}
        {textData.educationContent.howItHelpsTitle}
      </h2>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.educationContent.howItHelpsDescription}
      </p>

      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>{textData.educationContent.howItHelpsList[0]}</li>
        <li>{textData.educationContent.howItHelpsList[1]}</li>
        <li>{textData.educationContent.howItHelpsList[2]}</li>
        <li>{textData.educationContent.howItHelpsList[3]}</li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.educationContent.howToUseTitle}
        <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
          {textData.educationContent.followingDetail}
        </p>
      </h2>

      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <strong>{textData.educationContent.howToUseList[0].label}</strong>{" "}
          {textData.educationContent.howToUseList[0].description}
        </li>
        <li>
          <strong>{textData.educationContent.howToUseList[1].label}</strong>{" "}
          {textData.educationContent.howToUseList[1].description}
        </li>
        <li>
          <strong>{textData.educationContent.howToUseList[2].label}</strong>{" "}
          {textData.educationContent.howToUseList[2].description}
        </li>
      </ul>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.educationContent.howToUseSummary}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.educationContent.formulaTitle}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.educationContent.formulaDes}
      </p>
      <p className="font-medium bg-[#E0E4F0] text-sm sm:text-base p-3 mt-3">
        {textData.educationContent.formula}
      </p>
      <table className="border-collapse mt-4 w-full">
        <tbody>
          <tr className="bg-gray-100">
            {" "}
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              P
            </td>{" "}
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              {textData.educationContent.formulaParameters.P}
            </td>
          </tr>
          <tr className="bg-gray-100">
            {" "}
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              R
            </td>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              {textData.educationContent.formulaParameters.R}
            </td>
          </tr>
          <tr className="bg-gray-100">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              N
            </td>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              {textData.educationContent.formulaParameters.N}
            </td>
          </tr>
        </tbody>
      </table>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.educationContent.exampleTitle}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.educationContent.exampleDec}
      </p>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <strong>{textData.educationContent.example.LoanAmt}</strong>{" "}
          ₹10,00,000
        </li>
        <li>
          <strong>{textData.educationContent.example.RateInt}</strong> 6.5%{" "}
          {textData.educationContent.example.annually} = 0.54%{" "}
          {textData.educationContent.example.monthly}
        </li>
        <li>
          <strong>{textData.educationContent.example.Tenure}</strong> 5{" "}
          {textData.educationContent.example.years} = 60{" "}
          {textData.educationContent.example.months}
        </li>
        <li>
          <strong>{textData.educationContent.example.EMI}</strong> ₹19,575 (
          {textData.educationContent.example.approx}.)
        </li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.educationContent.benefitsTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>{textData.educationContent.benefitsList[0]}</li>
        <li>{textData.educationContent.benefitsList[1]}</li>
        <li>{textData.educationContent.benefitsList[2]}</li>
        <li>{textData.educationContent.benefitsList[3]}</li>
        <li>{textData.educationContent.benefitsList[4]}</li>
      </ul>
    </div>
  );
  const getPlaceholderValue = (language) => {
    const baseValue = 10000;
    const formatLang = language === "fa" ? "en-IN" : language;
    return new Intl.NumberFormat(formatLang).format(baseValue);
  };

  const timeLabel = t("yearShort");

  return (
    <>
      <Helmet>
        <title>Education Loan EMI Calculator | Education Loan Online</title>
        <meta
          name="description"
          content="Education Loan EMI Calculator by GrowLifeFund helps you analyze student loan EMIs, plan stress-free repayment for your studies and manage student loans."
        />
        <link
          rel="canonical"
          href="https://growlifefund.com/calculator/education-loan-calculator"
        />

        <meta
          property="og:title"
          content="Education Loan EMI Calculator | Education Loan Online"
        />
        <meta
          property="og:description"
          content="Education Loan EMI Calculator by GrowLifeFund helps you analyze student loan EMIs, plan stress-free repayment for your studies and manage student loans."
        />
        <meta
          property="og:url"
          content="https://growlifefund.com/calculator/education-loan-calculator"
        />
        <meta property="og:site_name" content="growlifefund" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en" />
        <meta
          property="og:image:alt"
          content="Education Loan EMI Calculator - GrowLifeFund"
        />

        <meta name="robots" content="index, follow" />
      </Helmet>

      <CalculatorLayout
        title={
          <>
            {" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.your}{" "}
            </span>
            {textData.educationCalc.eductionTitle}{" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.emis}
            </span>
          </>
        }
        description={textData.educationCalc.educationdiscription}
        descriptionContent={descriptionContent}
        setShowTable={setShowTable}
        showTable={showTable}
        calcDone={calcDone}
        setCalcDone={setCalcDone}
        isFormModified={isFormModified}
        setIsFormModified={setIsFormModified}
      >
        <div className="">
          <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-6">
            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start text-black sm:text-sm text-sm">
                  {textData.loanAmount}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={sliderTrackRef}>
                  {/* Background track */}
                  
                  <input
                    type="range"
                    min={100000}
                    max={5000000}
                    step={100000}
                    value={loanAmmount}
                    onChange={handleLoanAmountSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${
                        ((loanAmmount - 100000) / (5000000 - 100000)) * 100
                      }%`,
                    }}
                  />
                </div>
                <div className="flex items-center flex-col">
                  <Input
                    name="loanAmmount"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.loanAmmount)}
                    onChange={handleInvestmentChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getPlaceholderValue(i18n.language)}
                    // onChange={handleLoanAmountChange}
                    borderColor={
                      formik.touched.loanAmmount && formik.errors.loanAmmount
                        ? "border-danger"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px] ${
                      formik.touched.loanAmmount && formik.errors.loanAmmount
                        ? "border-danger"
                        : "border-gray-light"
                    } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.loanAmmount && formik.errors.loanAmmount && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.loanAmmount}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start text-black sm:text-sm text-sm">
                  {textData.rateOfInterest}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={interestSliderRef}>
                  {/* Background track */}
                  
                  <input
                    type="range"
                    min={1}
                    max={20}
                    step={0.1}
                    value={parseFloat(intrestRate)}
                    onChange={handleIntrestRatetSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((intrestRate - 1) / (20 - 1)) * 100}%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-center flex-col">
                  <Input
                    name="intrestRate"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("6.5")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.intrestRate
                    )}
                    onChange={handleInterestRateChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.intrestRate && formik.errors.intrestRate
                        ? "border-danger"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px] ${
                      formik.touched.intrestRate && formik.errors.intrestRate
                        ? "border-danger"
                        : "border-gray-light"
                    } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.intrestRate && formik.errors.intrestRate && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.intrestRate}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start text-black sm:text-sm text-sm">
                  {textData.timePeriod}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={periodSliderRef}>
                  {/* Background track */}
                  
                  <input
                    type="range"
                    min={1}
                    max={20}
                    step={1}
                    value={timePeriod}
                    onChange={handleTimePeriodSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((timePeriod - 1) / (20 - 1)) * 100}%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-center flex-col">
                  <Input
                    name="timePeriod"
                    leftIcon={timeLabel}
                    value={formik.values.timePeriod}
                    onChange={handleTimeChange}
                    onBlur={formik.handleBlur}
                    type="number"
                    placeholder={"5"}
                    borderColor={
                      formik.touched.timePeriod && formik.errors.timePeriod
                        ? "border-danger"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px] ${
                      formik.touched.timePeriod && formik.errors.timePeriod
                        ? "border-danger"
                        : "border-gray-light"
                    } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.timePeriod && formik.errors.timePeriod && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.timePeriod}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-[10px]">
              {" "}
              <Button type="submit" className="w-full">
                {textData.calculate}
              </Button>
            </div>
          </form>
        </div>
      </CalculatorLayout>
    </>
  );
};

export default EducationCalc;
